import React, { createContext, useContext, useState, useEffect } from 'react';
import dashboardService from '../services/dashboardService';
import { useAuth } from './AuthContext';

// Création du contexte
const DashboardContext = createContext();

/**
 * Fournisseur du contexte de tableau de bord
 * @param {Object} props - Propriétés du composant
 * @returns {React.ReactNode} - Fournisseur du contexte
 */
export const DashboardProvider = ({ children }) => {
    const { user, isAuthenticated } = useAuth();
    const [preferences, setPreferences] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Charger les préférences de l'utilisateur
    useEffect(() => {
        const loadPreferences = async () => {
            if (!isAuthenticated || !user) {
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);

                // Charger les préférences en fonction du rôle de l'utilisateur
                const userPreferences = user.isAdmin
                    ? await dashboardService.getAdminPreferences()
                    : await dashboardService.getUserPreferences();

                setPreferences(userPreferences);
            } catch (err) {
                console.error('Erreur lors du chargement des préférences:', err);
                setError('Impossible de charger les préférences du tableau de bord.');
                // Utiliser les préférences par défaut en cas d'erreur
                setPreferences(dashboardService.getDefaultPreferences());
            } finally {
                setLoading(false);
            }
        };

        loadPreferences();
    }, [user, isAuthenticated]);

    /**
     * Sauvegarder les préférences de l'utilisateur
     * @param {Object} newPreferences - Nouvelles préférences
     * @returns {Promise<boolean>} - Succès de l'opération
     */
    const savePreferences = async (newPreferences) => {
        try {
            setLoading(true);
            setError(null);

            const savedPreferences = await dashboardService.saveUserPreferences(newPreferences);
            setPreferences(savedPreferences);

            return true;
        } catch (err) {
            console.error('Erreur lors de la sauvegarde des préférences:', err);
            setError('Impossible de sauvegarder les préférences.');
            return false;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Mettre à jour un widget spécifique
     * @param {string} widgetId - ID du widget à mettre à jour
     * @param {Object} updates - Mises à jour à appliquer
     * @returns {Promise<boolean>} - Succès de l'opération
     */
    const updateWidget = async (widgetId, updates) => {
        try {
            if (!preferences || !preferences.widgets) return false;

            const updatedWidgets = preferences.widgets.map(widget =>
                widget.id === widgetId ? { ...widget, ...updates } : widget
            );

            const newPreferences = {
                ...preferences,
                widgets: updatedWidgets
            };

            const success = await savePreferences(newPreferences);
            return success;
        } catch (err) {
            console.error('Erreur lors de la mise à jour du widget:', err);
            setError('Impossible de mettre à jour le widget.');
            return false;
        }
    };

    /**
     * Mettre à jour la disposition des widgets
     * @param {Array} widgets - Nouvelle disposition des widgets
     * @returns {Promise<boolean>} - Succès de l'opération
     */
    const updateWidgetsLayout = async (widgets) => {
        try {
            setLoading(true);
            setError(null);

            const updatedPreferences = await dashboardService.updateWidgetsLayout(widgets);
            setPreferences(updatedPreferences);

            return true;
        } catch (err) {
            console.error('Erreur lors de la mise à jour de la disposition des widgets:', err);
            setError('Impossible de mettre à jour la disposition des widgets.');
            return false;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Mettre à jour le thème du tableau de bord
     * @param {string} theme - Nouveau thème
     * @param {Object} colors - Nouvelles couleurs (pour le thème personnalisé)
     * @returns {Promise<boolean>} - Succès de l'opération
     */
    const updateTheme = async (theme, colors = null) => {
        try {
            setLoading(true);
            setError(null);

            const updatedPreferences = await dashboardService.updateTheme(theme, colors);
            setPreferences(updatedPreferences);

            return true;
        } catch (err) {
            console.error('Erreur lors de la mise à jour du thème:', err);
            setError('Impossible de mettre à jour le thème.');
            return false;
        } finally {
            setLoading(false);
        }
    };

    /**
     * Réinitialiser les préférences aux valeurs par défaut
     * @returns {Promise<boolean>} - Succès de l'opération
     */
    const resetToDefault = async () => {
        try {
            setLoading(true);
            setError(null);

            const defaultPreferences = await dashboardService.resetToDefault();
            setPreferences(defaultPreferences);

            return true;
        } catch (err) {
            console.error('Erreur lors de la réinitialisation des préférences:', err);
            setError('Impossible de réinitialiser les préférences.');
            return false;
        } finally {
            setLoading(false);
        }
    };

    // Valeur du contexte
    const value = {
        preferences,
        loading,
        error,
        savePreferences,
        updateWidget,
        updateWidgetsLayout,
        updateTheme,
        resetToDefault
    };

    return (
        <DashboardContext.Provider value={value}>
            {children}
        </DashboardContext.Provider>
    );
};

/**
 * Hook pour utiliser le contexte de tableau de bord
 * @returns {Object} - Contexte de tableau de bord
 */
export const useDashboard = () => {
    const context = useContext(DashboardContext);
    if (context === undefined) {
        throw new Error('useDashboard doit être utilisé à l\'intérieur d\'un DashboardProvider');
    }
    return context;
};

export default DashboardContext;